import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import HeaderAccount from "../../../../components/HeaderAccount";
import { useTranslation } from "react-i18next";
import axiosWithAuth from "../../../../utils/axiosWithAuth";
import { getApiEndpoint } from "../../../../utils/apiConfig";
import { SquareLoader } from "react-spinners";
import RoomDropdown from "../../../../components/RoomDropdown/RoomDropdown";
import "./_HomePlanDetails.scss";

const HomePlanDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [calculation, setCalculation] = useState({});
  const [breakdownData, setBreakdownData] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [costs, setCosts] = useState(null);
  
  document.title = t("homePlanDetails.0");
  const headerTitle = t("homePlanDetails.0");
  const backwardNavigate = "/account/homeplan";

  useEffect(() => {
    const fetchCalculationDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("Fetching calculation details for ID:", id);
        const api = axiosWithAuth();
        
        // Try to get calculation details first
        let calculationResponse;
        try {
          calculationResponse = await api.get(getApiEndpoint(`repairs/calculations/${id}`));
        } catch (calcError) {
          console.log("Direct calculation fetch failed, trying list approach");
          // If direct fetch fails, try to get from the list
          const listResponse = await api.get(getApiEndpoint('repairs/calculations'));
          if (listResponse.data && listResponse.data.success) {
            const foundCalculation = listResponse.data.data.find(calc => calc._id === id);
            if (foundCalculation) {
              calculationResponse = { data: { success: true, data: foundCalculation } };
            } else {
              throw new Error("Calculation not found");
            }
          } else {
            throw new Error("Failed to load calculation details");
          }
        }

        if (calculationResponse.data && calculationResponse.data.success) {
          const calcData = calculationResponse.data.data;
          setCalculation(calcData);
          
          // Now get the breakdown materials
          try {
            const breakdownResponse = await api.get(`${getApiEndpoint('repairs/breakdown-materials')}/${id}`);
            if (breakdownResponse.data) {
              setBreakdownData(breakdownResponse.data);
              if (breakdownResponse.data.breakdown && breakdownResponse.data.breakdown.rooms) {
                setRooms(breakdownResponse.data.breakdown.rooms);
                setFilteredRooms(breakdownResponse.data.breakdown.rooms);
              }
              if (breakdownResponse.data.costs) {
                setCosts(breakdownResponse.data.costs);
              }
            }
          } catch (breakdownError) {
            console.log("Breakdown fetch failed, using calculation data");
            // Fallback to calculation data
            if (calcData.breakdown) {
              setBreakdownData({ breakdown: calcData.breakdown, costs: calcData.costs });
              setRooms(calcData.breakdown.rooms || []);
              setFilteredRooms(calcData.breakdown.rooms || []);
              setCosts(calcData.costs);
            }
          }
        } else {
          setError("Calculation not found");
        }
      } catch (error) {
        console.error("Error fetching calculation details:", error);
        setError("Failed to load calculation details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCalculationDetails();
    }
  }, [id]);

  const handleRoomChange = (selectedRoom) => {
    setSelectedRoom(selectedRoom);
    
    if (selectedRoom && rooms.length > 0) {
      const roomIndex = rooms.findIndex(room => 
        room.roomType.replace(/\s+/g, '-').toLowerCase() === selectedRoom.id
      );
      
      if (roomIndex !== -1) {
        setFilteredRooms([rooms[roomIndex]]);
      } else {
        setFilteredRooms([]);
      }
    } else {
      setFilteredRooms([]);
    }
  };

  const renderMaterialRow = (material, depth = 0) => {
    const room = filteredRooms[0];
    let roomArea = 16; // Default fallback
    
    if (room && room.area) {
      roomArea = parseFloat(room.area);
    }
    
    const quantity = parseFloat(material.quantity || material.count || 1);
    const price = parseFloat(material.price || 0);
    const servicePrice = parseFloat(material.servicePrice || 0);
    
    const materialCost = price * quantity * roomArea;
    const serviceCost = servicePrice * quantity * roomArea;
    
    return (
      <React.Fragment key={`mat-${depth}-${material.id}`}>
        <div className={`table-row${depth > 0 ? ' dependent-row' : ''}`} style={{ paddingLeft: `${depth * 20}px` }}>
          <div className="column name-column">
            {depth > 0 && (
              <span className="dependent-prefix" style={{ marginRight: '8px' }}>
                {depth > 1 ? '└─' : '├─'}
              </span>
            )}
            <span className="material-name">
              {material.materialName || (typeof material.name === 'object' ? material.name.text || material.name.az : material.name)}
            </span>
            {quantity > 1 && (
              <span className="material-quantity">
                ({quantity} units)
              </span>
            )}
            {roomArea > 1 && (
              <span className="material-area" style={{ marginLeft: '8px', color: '#666' }}>
                (x{roomArea} m²)
              </span>
            )}
          </div>
          <div className="column store-column">
            {material.store?.name || material.storeName || t("material.noStore")}
          </div>
          <div className="column price-column">
            {price.toFixed(2)}
          </div>
          <div className="column amount-column">
            {materialCost.toFixed(2)}
          </div>
        </div>
        
        {material.dependentMaterials?.map((depMaterial, index) => 
          renderMaterialRow(depMaterial, depth + 1)
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div className="loader-container">
        <SquareLoader color="#cda742" size={80} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-plan-details-section mx-3">
        <HeaderAccount title={headerTitle} backwardNavigate={backwardNavigate} />
        <div className="alert alert-danger mt-3">{error}</div>
      </div>
    );
  }

  if (!calculation || !calculation._id) {
    return (
      <div className="home-plan-details-section mx-3">
        <HeaderAccount title={headerTitle} backwardNavigate={backwardNavigate} />
        <div className="alert alert-info mt-3">Hesablama məlumatları tapılmadı</div>
      </div>
    );
  }

  return (
    <div className="home-plan-details-section mx-3">
      <HeaderAccount title={headerTitle} backwardNavigate={backwardNavigate} />
      
      {/* Calculation Summary */}
      {costs && (
        <div className="calculation-summary">
          <div className="summary-header">
            <h5>Hesablama Məlumatları</h5>
            <div className="calculation-id">ID: {calculation.tempId || calculation._id.slice(-6)}</div>
          </div>
          
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-title">{t("material.materials")}</div>
              <div className="summary-value">
                {costs.materialsCost?.toFixed(2) || '0.00'} {costs.currency || "AZN"}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-title">{t("material.service")}</div>
              <div className="summary-value">
                {costs.serviceCost?.toFixed(2) || '0.00'} {costs.currency || "AZN"}
              </div>
            </div>
            <div className="summary-card total">
              <div className="summary-title">{t("material.total")}</div>
              <div className="summary-value">
                {costs.generalCost?.toFixed(2) || '0.00'} {costs.currency || "AZN"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Materials Content */}
      <div className="materials-content">
        <div className="top-section">
          <h5>{t("material.0")}</h5>
          <p className="subtitle">{t("material.1")}</p>
          </div>
        
        <div className="middle-section">
          {rooms.length > 0 ? (
            <RoomDropdown
              onChange={handleRoomChange}
              selectedRoomTypes={rooms.map(room => ({
                id: room.roomType.replace(/\s+/g, '-').toLowerCase(),
                name: room.roomType
              }))}
              selectedValue={selectedRoom}
            />
          ) : (
            <div className="no-rooms-message">
              {t("material.7")}
          </div>
          )}
        </div>
        
        {/* Materials listing */}
        {filteredRooms.length > 0 && (
          <div className="bottom-section breakdown-view">
            <div className="room-header">
              <h6>
                {filteredRooms[0]?.roomType || 'Otaq'} - 
                <span className="room-area">
                  {filteredRooms[0]?.area || 16} m²
                </span>
              </h6>
            </div>
            
            <div className="materials-table">
              <div className="table-header">
                <div className="column name-column">{t("material.2")}</div>
                <div className="column store-column">{t("material.store")}</div>
                <div className="column price-column">{t("material.price")}</div>
                <div className="column amount-column">{t("material.total")}</div>
      </div>

              <div className="table-body">
                {filteredRooms[0].materials && filteredRooms[0].materials.map((material) => 
                  renderMaterialRow(material)
                )}
              </div>
            </div>
            
            {/* Room total */}
            <div className="room-total">
              <span>{t("material.total")}:</span>
              <span className="total-price">
                {(() => {
                  const room = filteredRooms[0];
                  if (!room) return "0.00";
                  
                  const roomArea = parseFloat(room.area || 16);
                  let totalCost = 0;
                  
                  room.materials?.forEach(material => {
                    const quantity = parseFloat(material.quantity || material.count || 1);
                    const price = parseFloat(material.price || 0);
                    const servicePrice = parseFloat(material.servicePrice || 0);
                    
                    totalCost += (price + servicePrice) * quantity * roomArea;
                    
                    material.dependentMaterials?.forEach(depMat => {
                      const depQuantity = parseFloat(depMat.quantity || depMat.count || 1);
                      const depPrice = parseFloat(depMat.price || 0);
                      const depServicePrice = parseFloat(depMat.servicePrice || 0);
                      
                      totalCost += (depPrice + depServicePrice) * depQuantity * roomArea;
                    });
                  });
                  
                  return totalCost.toFixed(2);
                })()} {costs?.currency || "AZN"}
              </span>
            </div>
            
            {/* Fixtures */}
            {breakdownData?.breakdown?.fixtures && (
              <div className="fixtures-section">
                <h6>{t("material.fixtures")}</h6>
                
                <div className="materials-table">
                  <div className="table-header">
                    <div className="column name-column">{t("material.item")}</div>
                    <div className="column count-column">{t("material.count")}</div>
                    <div className="column amount-column">{t("material.cost")}</div>
                  </div>
                  
                  {breakdownData.breakdown.fixtures.doors && 
                   breakdownData.breakdown.fixtures.doors.count > 0 && (
                    <div className="table-row">
                      <div className="column name-column">{t("material.doors")}</div>
                      <div className="column count-column">{breakdownData.breakdown.fixtures.doors.count}</div>
                      <div className="column amount-column">
                        {(
                          parseFloat(breakdownData.breakdown.fixtures.doors.materialCost || 0) +
                          parseFloat(breakdownData.breakdown.fixtures.doors.serviceCost || 0)
                        ).toFixed(2)} {costs?.currency || "AZN"}
                      </div>
                    </div>
                  )}
                  
                  {breakdownData.breakdown.fixtures.windows && 
                   breakdownData.breakdown.fixtures.windows.count > 0 && (
                    <div className="table-row">
                      <div className="column name-column">{t("material.windows")}</div>
                      <div className="column count-column">{breakdownData.breakdown.fixtures.windows.count}</div>
                      <div className="column amount-column">
                        {(
                          parseFloat(breakdownData.breakdown.fixtures.windows.materialCost || 0) +
                          parseFloat(breakdownData.breakdown.fixtures.windows.serviceCost || 0)
                        ).toFixed(2)} {costs?.currency || "AZN"}
                      </div>
                    </div>
                  )}
                  
                  {(!breakdownData.breakdown.fixtures.doors || breakdownData.breakdown.fixtures.doors.count <= 0) &&
                   (!breakdownData.breakdown.fixtures.windows || breakdownData.breakdown.fixtures.windows.count <= 0) && (
                    <div className="table-row">
                      <div className="column name-column" style={{textAlign: "center"}}>
                        {t("material.noFixtures")}
                    </div>
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>
        )}
        
        {!filteredRooms.length && (
          <div className="no-materials">
            {t("material.noMaterialsSelected")}
        </div>
        )}
      </div>
    </div>
  );
};

export default HomePlanDetails;
